import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Building2, Flame, Trophy } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { DataTable } from '@/components/app/DataTable';
import { ErrorState } from '@/components/app/ErrorState';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { humanizeError } from '@/lib/apiClient';
import { useTeamsList } from '../hooks';
import type { TeamListItem, TeamStatus } from '../types';

const STATUS_FILTERS: { value: 'all' | TeamStatus; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'warned', label: 'Warned' },
  { value: 'frozen', label: 'Frozen' },
  { value: 'terminated', label: 'Terminated' },
];

function statusVariant(s: TeamStatus): 'success' | 'warning' | 'destructive' | 'outline' {
  if (s === 'active') return 'success';
  if (s === 'warned') return 'warning';
  if (s === 'frozen') return 'outline';
  return 'destructive';
}

export function TeamsListPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = React.useState<'all' | TeamStatus>('all');
  const [cursorStack, setCursorStack] = React.useState<string[]>([]);
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);

  const params = {
    limit: 50,
    cursor,
    status: statusFilter === 'all' ? undefined : (statusFilter as TeamStatus),
  };
  const teamsQuery = useTeamsList(params);

  const columns = React.useMemo<ColumnDef<TeamListItem>[]>(() => {
    return [
      {
        id: 'team',
        header: 'Team',
        cell: ({ row }) => (
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{row.original.teamName}</div>
              <div className="truncate font-mono text-xs text-muted-foreground">
                {row.original.teamId}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={statusVariant(row.original.status)}>{row.original.status}</Badge>
        ),
      },
      {
        id: 'members',
        header: 'Members',
        cell: ({ row }) => (
          <span className="text-sm">{row.original.memberCount}</span>
        ),
      },
      {
        id: 'streak',
        header: 'Streak',
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1 text-sm">
            <Flame className="h-3 w-3 text-amber-500" />
            {row.original.teamStreak ?? 0}d
          </span>
        ),
      },
      {
        id: 'level',
        header: 'Level',
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1 text-sm">
            <Trophy className="h-3 w-3" />
            {row.original.teamLevel ?? '—'}
          </span>
        ),
      },
      {
        id: 'created',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {row.original.createdAt.slice(0, 10)}
          </span>
        ),
      },
    ];
  }, []);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Teams"
        description="Inspect any team. Freeze, warn, or terminate from the detail page."
        actions={
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as 'all' | TeamStatus);
              setCursor(undefined);
              setCursorStack([]);
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      {teamsQuery.isError ? (
        <ErrorState
          title="Teams failed to load"
          description={humanizeError(teamsQuery.error)}
          actionLabel="Retry"
          onAction={() => teamsQuery.refetch()}
        />
      ) : (
        <DataTable<TeamListItem>
          columns={columns}
          data={teamsQuery.data?.teams ?? []}
          loading={teamsQuery.isLoading || teamsQuery.isFetching}
          emptyLabel="No teams"
          rowKey={(r) => r.teamId}
          onRowClick={(r) => navigate(`/teams/${encodeURIComponent(r.teamId)}`)}
          pagination={{
            cursor: cursor ?? null,
            nextCursor: teamsQuery.data?.nextCursor ?? null,
            prevCursor: cursorStack.length > 0 ? (cursorStack[cursorStack.length - 1] ?? null) : null,
            hasMore: !!teamsQuery.data?.nextCursor,
            onNext: teamsQuery.data?.nextCursor
              ? () => {
                  setCursorStack((stack) => [...stack, cursor ?? '']);
                  setCursor(teamsQuery.data?.nextCursor ?? undefined);
                }
              : undefined,
            onPrev:
              cursorStack.length > 0
                ? () => {
                    const prev = cursorStack[cursorStack.length - 1] || undefined;
                    setCursor(prev);
                    setCursorStack((stack) => stack.slice(0, -1));
                  }
                : undefined,
          }}
        />
      )}
    </div>
  );
}
